import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  TextField,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Stack,
  Chip,
  IconButton,
  Collapse,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormLabel,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useLocation, useNavigate } from 'react-router-dom'
import { fuzzyMatch } from '../utils/textUtils'

// Custom debounce hook
function useDebounce<T extends (...args: any[]) => any>(
  callback: T,
  delay: number,
): (...args: Parameters<T>) => void {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const debouncedCallback = useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        callback(...args)
      }, delay)
    },
    [callback, delay],
  )

  return debouncedCallback
}

// Filter configuration types
export interface FilterFieldConfig {
  key: string
  label: string
  type: 'select' | 'autocomplete' | 'checkbox' | 'date' | 'dateRange' | 'radio'
  options?: Array<{ value: any; label: string }>
  multiple?: boolean
  size?: { minWidth?: number; maxWidth?: number }
  dependencies?: string[] // Fields that affect this field's visibility
  condition?: (values: Record<string, any>) => boolean // Custom visibility condition
}

export interface FilterRowConfig {
  fields: FilterFieldConfig[]
  groups?: Array<{
    label: string
    fieldKeys: string[] // Which fields belong to this group
  }>
}

export interface SearchConfig {
  searchPlaceholder: string
  searchFields: string[] // URL parameters to clear when searching
  hasAdvancedFilters: boolean
  advancedFilterRows: FilterRowConfig[]
  opportunityFilterRows?: FilterRowConfig[] // Optional opportunity-level filters for funders page
  chipFilters: string[] // Parameters that should show as chips
  customComponents?: Record<string, React.ComponentType<any>> // Custom filter components
  customChipFormatters?: Record<
    string,
    (value: any, allFilters?: Record<string, any>) => string
  > // Custom chip formatting
}

interface UnifiedQuickSearchProps {
  config: SearchConfig
  onSearchChange?: (filters: any) => void
  customData?: Record<string, any> // For passing data to custom components (e.g., regions, users)
  resourceName: string // Used for localStorage key uniqueness
}

export default function UnifiedQuickSearch({
  config,
  onSearchChange,
  customData = {},
  resourceName,
}: UnifiedQuickSearchProps) {
  const location = useLocation()
  const navigate = useNavigate()

  // Helper function to get localStorage key
  const getStorageKey = () =>
    `unified-quick-search-${resourceName}-advanced-filters-open`

  // Initialize advanced filters state from localStorage immediately
  const initializeAdvancedFiltersState = () => {
    try {
      const stored = localStorage.getItem(getStorageKey())
      return stored ? JSON.parse(stored) : false
    } catch {
      return false
    }
  }

  // State - initialize showAdvancedFilters directly from localStorage
  const [searchText, setSearchText] = useState('')
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(() =>
    initializeAdvancedFiltersState(),
  )
  const [filterValues, setFilterValues] = useState<Record<string, any>>({})

  // Parse initial values from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const initialValues: Record<string, any> = {}

    // Search text
    const q = params.get('q')
    if (q) setSearchText(q)

    // Parse all filter values from URL
    config.advancedFilterRows.forEach((row) => {
      row.fields.forEach((field) => {
        const value = params.get(field.key)
        if (value !== null) {
          if (field.type === 'checkbox') {
            initialValues[field.key] = value === 'true'
          } else if (field.type === 'date' || field.type === 'dateRange') {
            initialValues[field.key] = value ? new Date(value) : null
          } else if (field.type === 'autocomplete') {
            // Try to parse JSON for autocomplete values
            try {
              initialValues[field.key] = JSON.parse(value)
            } catch {
              initialValues[field.key] = value
            }
          } else {
            initialValues[field.key] = value
          }
        }
      })
    })

    // Parse dynamic date range fields that aren't explicitly in config
    // Look for any parameter that ends with _start_date or _end_date
    for (const [key, value] of params.entries()) {
      if ((key.endsWith('_start_date') || key.endsWith('_end_date')) && value) {
        try {
          initialValues[key] = new Date(value)
        } catch {
          initialValues[key] = value
        }
      }
    }

    // Parse URL parameters that are in chipFilters but not in field definitions
    // This handles computed flags and other parameters that don't have UI controls
    config.chipFilters.forEach((chipKey) => {
      if (!Object.prototype.hasOwnProperty.call(initialValues, chipKey)) {
        const value = params.get(chipKey)
        if (value !== null) {
          // For boolean-like parameters, convert string to boolean
          if (value === 'true' || value === 'false') {
            initialValues[chipKey] = value === 'true'
          } else {
            initialValues[chipKey] = value
          }
        }
      }
    })

    setFilterValues(initialValues)
  }, [location.search, config])

  // Save advanced filters state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(getStorageKey(), JSON.stringify(showAdvancedFilters))
    } catch {
      // Handle localStorage errors silently
    }
  }, [showAdvancedFilters, resourceName])

  // Update URL with filters
  const updateFilters = useCallback(
    (updates: Record<string, any>) => {
      const params = new URLSearchParams(location.search)

      // Clear search-related params if specified
      config.searchFields.forEach((key) => params.delete(key))

      // Apply updates
      Object.entries(updates).forEach(([key, value]) => {
        if (
          value !== null &&
          value !== '' &&
          value !== 'all' &&
          value !== false
        ) {
          if (value instanceof Date) {
            params.set(key, value.toISOString())
          } else if (typeof value === 'object' && value !== null) {
            // For objects (like autocomplete selections), store as JSON
            params.set(key, JSON.stringify(value))
          } else {
            params.set(key, String(value))
          }
        } else {
          params.delete(key)
        }
      })

      // Reset pagination
      params.set('current', '1')

      const newUrl = `${location.pathname}?${params.toString()}`
      navigate(newUrl)
      onSearchChange?.(updates)
    },
    [
      location.pathname,
      location.search,
      navigate,
      onSearchChange,
      config.searchFields,
    ],
  )

  // Debounced search handler
  const debouncedUpdateFilters = useDebounce(updateFilters, 500)

  // Handle search text change
  const handleSearchChange = (value: string) => {
    setSearchText(value)
    const updates: Record<string, any> = { q: value.trim(), ...filterValues }
    debouncedUpdateFilters(updates)
  }

  // Handle filter changes (immediate)
  const handleFilterChange = (key: string, value: any) => {
    const newFilterValues: Record<string, any> = {
      ...filterValues,
      [key]: value,
    }

    const updates: Record<string, any> = {
      q: searchText.trim(),
      ...newFilterValues,
    }

    // Special handling for date_range_type in opportunities
    if (key === 'date_range_type' && resourceName === 'opportunities') {
      const today = new Date()

      // Clear existing date parameters while preserving the current field value
      updates.date_start = null
      updates.date_end = null
      updates.no_check_in_date = null
      // Also clear custom date range fields when switching away from custom
      updates.date_range_start_date = null
      updates.date_range_end_date = null
      newFilterValues.date_range_start_date = null
      newFilterValues.date_range_end_date = null

      switch (value) {
        case 'no_date':
          updates.no_check_in_date = 'true'
          break
        case 'overdue':
          updates.date_end = today.toISOString()
          break
        case 'next_week': {
          updates.date_start = today.toISOString()
          const nextWeek = new Date(today)
          nextWeek.setDate(today.getDate() + 7)
          updates.date_end = nextWeek.toISOString()
          break
        }
        case 'next_month': {
          updates.date_start = today.toISOString()
          const nextMonth = new Date(today)
          nextMonth.setMonth(today.getMonth() + 1)
          updates.date_end = nextMonth.toISOString()
          break
        }
        case 'custom':
          // Keep existing dates if any, don't clear them
          break
        default:
          // Clear all date filters
          break
      }
    }

    // Special handling for funding_rounds_type in opportunities
    if (key === 'funding_rounds_type' && resourceName === 'opportunities') {
      // Clear existing funding parameters while preserving the current field value
      updates.funding_start_date = null
      updates.funding_end_date = null
      updates.no_funding_rounds = null
      updates.always_open = null
      updates.open_now = null
      updates.past_only = null
      // Also clear custom funding date range fields when switching away from custom
      updates.funding_rounds_start_date = null
      updates.funding_rounds_end_date = null
      newFilterValues.funding_rounds_start_date = null
      newFilterValues.funding_rounds_end_date = null

      switch (value) {
        case 'no_rounds':
          updates.no_funding_rounds = 'true'
          updates.always_open = 'false'
          break
        case 'always_open':
          updates.always_open = 'true'
          break
        case 'open_now':
          updates.open_now = 'true'
          break
        case 'past_only':
          updates.past_only = 'true'
          break
        case 'custom':
          // Keep existing funding dates if any, don't clear them
          break
        default:
          // Clear all funding filters
          break
      }
    }

    // Generic handling for custom date range fields
    if (key.endsWith('_start_date') || key.endsWith('_end_date')) {
      // For custom date range fields, we need to set the corresponding *_type to 'custom'
      const baseKey = key.replace('_start_date', '').replace('_end_date', '')
      const typeKey = `${baseKey}_type`

      // If there's a corresponding type field, set it to 'custom'
      if (
        config.advancedFilterRows.some((row) =>
          row.fields.some((field) => field.key === typeKey),
        )
      ) {
        updates[typeKey] = 'custom'
        // Update the local state immediately to ensure consistency
        newFilterValues[typeKey] = 'custom'

        // Map custom date range fields to the expected backend parameter names
        if (resourceName === 'opportunities') {
          if (key === 'date_range_start_date') {
            updates.date_start = value ? value.toISOString() : null
          } else if (key === 'date_range_end_date') {
            updates.date_end = value ? value.toISOString() : null
          } else if (key === 'funding_rounds_start_date') {
            updates.funding_start_date = value ? value.toISOString() : null
          } else if (key === 'funding_rounds_end_date') {
            updates.funding_end_date = value ? value.toISOString() : null
          }
        }
      }
    }

    // Update local state with all changes including any automatic type updates
    setFilterValues(newFilterValues)
    updateFilters(updates)
  }

  // Render a filter row with optional field groups
  const renderFilterRow = (row: FilterRowConfig) => {
    if (!row.groups || row.groups.length === 0) {
      // No groups, render all fields normally
      return row.fields.map((field) => renderFilterField(field))
    }

    // Has groups - separate fields into grouped and ungrouped
    const groupedFieldKeys = new Set(row.groups.flatMap((g) => g.fieldKeys))
    const ungroupedFields = row.fields.filter(
      (f) => !groupedFieldKeys.has(f.key),
    )

    return (
      <>
        {/* Render ungrouped fields first */}
        {ungroupedFields.map((field) => renderFilterField(field))}

        {/* Render each group */}
        {row.groups.map((group, groupIndex) => {
          const groupFields = row.fields.filter((f) =>
            group.fieldKeys.includes(f.key),
          )

          return (
            <Box
              key={`group-${groupIndex}`}
              component="fieldset"
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
                display: 'inline-block',
                m: 0,
              }}
            >
              <Stack
                direction="row"
                spacing={2}
                flexWrap="wrap"
                useFlexGap
                alignItems="center"
              >
                <Box
                  component="span"
                  sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}
                >
                  {group.label}:
                </Box>
                {groupFields.map((field) => renderFilterField(field))}
              </Stack>
            </Box>
          )
        })}
      </>
    )
  }

  // Render filter field based on type
  const renderFilterField = (field: FilterFieldConfig) => {
    const value = filterValues[field.key]

    // Check visibility conditions
    if (field.condition && !field.condition(filterValues)) {
      return null
    }

    // Check dependencies
    if (field.dependencies) {
      const shouldShow = field.dependencies.some((dep) => filterValues[dep])
      if (!shouldShow) return null
    }

    const commonProps = {
      size: 'small' as const,
      sx: {
        minWidth: field.size?.minWidth || 120,
        maxWidth: field.size?.maxWidth,
      },
    }

    switch (field.type) {
      case 'select': {
        const isDateRangeField =
          field.key.includes('_type') &&
          field.options?.some((opt) => opt.value === 'custom')
        const showCustomDateRange = isDateRangeField && value === 'custom'

        return (
          <React.Fragment key={field.key}>
            <FormControl {...commonProps}>
              <InputLabel>{field.label}</InputLabel>
              <Select
                value={value || 'all'}
                label={field.label}
                onChange={(e) =>
                  handleFilterChange(
                    field.key,
                    e.target.value === 'all' ? null : e.target.value,
                  )
                }
              >
                <MenuItem value="all">All</MenuItem>
                {field.options?.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Show custom date range pickers when "custom" is selected */}
            {showCustomDateRange && (
              <>
                <DatePicker
                  label={`${field.label
                    .replace('Date', '')
                    .replace('Type', '')
                    .trim()} Start`}
                  value={
                    filterValues[`${field.key.replace('_type', '')}_start_date`]
                      ? new Date(
                          filterValues[
                            `${field.key.replace('_type', '')}_start_date`
                          ],
                        )
                      : null
                  }
                  onChange={(date) =>
                    handleFilterChange(
                      `${field.key.replace('_type', '')}_start_date`,
                      date,
                    )
                  }
                  slotProps={{ textField: { size: 'small' } }}
                />
                <DatePicker
                  label={`${field.label
                    .replace('Date', '')
                    .replace('Type', '')
                    .trim()} End`}
                  value={
                    filterValues[`${field.key.replace('_type', '')}_end_date`]
                      ? new Date(
                          filterValues[
                            `${field.key.replace('_type', '')}_end_date`
                          ],
                        )
                      : null
                  }
                  onChange={(date) =>
                    handleFilterChange(
                      `${field.key.replace('_type', '')}_end_date`,
                      date,
                    )
                  }
                  slotProps={{ textField: { size: 'small' } }}
                />
              </>
            )}
          </React.Fragment>
        )
      }

      case 'autocomplete':
        return (
          <Autocomplete
            key={field.key}
            value={value || null}
            onChange={(event, newValue) =>
              handleFilterChange(field.key, newValue)
            }
            options={field.options || []}
            getOptionLabel={(option) => option.label || ''}
            isOptionEqualToValue={(option, value) => {
              if (!option || !value) return option === value
              // Handle object comparison for regions
              if (
                typeof option.value === 'object' &&
                typeof value.value === 'object'
              ) {
                return option.value.id === value.value.id
              }
              return option.value === value.value
            }}
            filterOptions={(options, { inputValue }) => {
              // Use fuzzy matching for type field (macron-aware, word-boundary prefix matching)
              if (field.key === 'type') {
                if (!inputValue) return options
                return options.filter((option) =>
                  fuzzyMatch(option.label, inputValue),
                )
              }

              // Custom filtering for region search - show top matches first
              if (!inputValue) return options.slice(0, 50) // Show first 50 when no input

              const searchTerm = inputValue.toLowerCase().trim()
              if (searchTerm.length < 2) return options.slice(0, 50) // Require at least 2 characters

              // Score options based on match quality
              const scoredOptions = options
                .map((option) => {
                  const label = option.label.toLowerCase()
                  let score = 0

                  // Exact match gets highest score
                  if (label === searchTerm) score = 1000
                  // Starts with search term gets high score
                  else if (label.startsWith(searchTerm)) score = 500
                  // Contains search term gets medium score
                  else if (label.includes(searchTerm)) score = 100
                  // No match
                  else return null

                  // Boost score for shorter names (more specific matches)
                  score += Math.max(0, 100 - label.length)

                  return { option, score }
                })
                .filter((item) => item !== null)
                .sort((a, b) => b!.score - a!.score)
                .map((item) => item!.option)

              // Return top 100 matches to avoid overwhelming the dropdown
              return scoredOptions.slice(0, 100)
            }}
            renderInput={(params) => (
              <TextField {...params} label={field.label} size="small" />
            )}
            sx={{ minWidth: field.size?.minWidth || 200 }}
            ListboxProps={{
              style: {
                maxHeight: 200,
              },
            }}
          />
        )

      case 'checkbox':
        return (
          <FormControlLabel
            key={field.key}
            control={
              <Checkbox
                checked={value || false}
                onChange={(e) =>
                  handleFilterChange(field.key, e.target.checked)
                }
                size="small"
              />
            }
            label={field.label}
            sx={{ minWidth: field.size?.minWidth || 150 }}
          />
        )

      case 'date':
        return (
          <DatePicker
            key={field.key}
            label={field.label}
            value={value || null}
            onChange={(date) => handleFilterChange(field.key, date)}
            slotProps={{ textField: { size: 'small' } }}
          />
        )

      case 'dateRange': {
        // For date ranges, we expect the field to have start/end keys
        const startKey = `${field.key}_start`
        const endKey = `${field.key}_end`
        return (
          <React.Fragment key={field.key}>
            <DatePicker
              label={`${field.label} Start`}
              value={filterValues[startKey] || null}
              onChange={(date) => handleFilterChange(startKey, date)}
              slotProps={{ textField: { size: 'small' } }}
            />
            <DatePicker
              label={`${field.label} End`}
              value={filterValues[endKey] || null}
              onChange={(date) => handleFilterChange(endKey, date)}
              slotProps={{ textField: { size: 'small' } }}
            />
          </React.Fragment>
        )
      }

      case 'radio':
        return (
          <Box
            key={field.key}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: field.size?.minWidth || 200,
            }}
          >
            {field.label && (
              <Box component="span" sx={{ fontSize: '0.875rem' }}>
                {field.label}
              </Box>
            )}
            <RadioGroup
              row
              value={value || field.options?.[0]?.value || ''}
              onChange={(e) => handleFilterChange(field.key, e.target.value)}
            >
              {field.options?.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio size="small" />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
          </Box>
        )

      default:
        return null
    }
  }

  // Render filter chips
  const renderFilterChips = () => {
    const chips: React.ReactNode[] = []

    // Search text chip
    if (searchText) {
      chips.push(
        <Chip
          key="search"
          label={`Search: ${searchText}`}
          onDelete={() => {
            setSearchText('')
            updateFilters({ q: null, ...filterValues })
          }}
          size="small"
        />,
      )
    }

    // Filter chips
    config.chipFilters.forEach((key) => {
      const value = filterValues[key]
      if (value) {
        // Skip showing chips for range type fields when set to "custom"
        // The individual start/end date chips will show the actual dates instead
        if (key.endsWith('_type') && value === 'custom') {
          return
        }

        // Skip showing start/end date chips when their corresponding type field is NOT set to "custom"
        // Only show them when user has explicitly selected custom dates
        if (key.endsWith('_start_date') || key.endsWith('_end_date')) {
          const baseKey = key
            .replace('_start_date', '')
            .replace('_end_date', '')
          const typeKey = `${baseKey}_type`
          const typeValue = filterValues[typeKey]

          // Only show start/end date chips when the type is explicitly set to "custom"
          if (typeValue !== 'custom') {
            return
          }
        }

        const formatter = config.customChipFormatters?.[key]
        let label = formatter
          ? formatter(value, filterValues)
          : `${key}: ${value}`

        // Special handling for assigned_to to look up user name from options
        if (key === 'assigned_to' && value !== 'unassigned') {
          // Find the field configuration to get the options
          let fieldOptions: any[] = []
          config.advancedFilterRows.forEach((row) => {
            const field = row.fields.find((f) => f.key === 'assigned_to')
            if (field?.options) {
              fieldOptions = field.options
            }
          })

          // Look up the user name from options
          const userOption = fieldOptions.find(
            (option: any) => option.value === value,
          )
          if (userOption) {
            label = `Assigned: ${userOption.label}`
          } else {
            label = formatter ? formatter(value) : `Assigned: ${value}`
          }
        }

        chips.push(
          <Chip
            key={key}
            label={label}
            onDelete={() => handleFilterChange(key, null)}
            size="small"
            color="primary"
          />,
        )
      }
    })

    return chips.length > 0 ? (
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {chips}
      </Stack>
    ) : null
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ mb: 3 }}>
        <Stack spacing={2}>
          {/* Search Bar with Advanced Filters Toggle */}
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              value={searchText}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={config.searchPlaceholder}
              variant="outlined"
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            {config.hasAdvancedFilters && (
              <IconButton
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                size="small"
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  width: 40,
                  height: 40,
                }}
              >
                {showAdvancedFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
          </Stack>

          {/* Advanced Filters - Collapsible or Always Visible */}
          {config.hasAdvancedFilters ? (
            <Collapse in={showAdvancedFilters}>
              <Stack spacing={2} sx={{ mt: 3 }}>
                {config.advancedFilterRows.map((row, rowIndex) => (
                  <Stack
                    key={rowIndex}
                    direction="row"
                    spacing={2}
                    flexWrap="wrap"
                    useFlexGap
                    alignItems="center"
                  >
                    {renderFilterRow(row)}
                  </Stack>
                ))}
              </Stack>
            </Collapse>
          ) : config.advancedFilterRows.length > 0 ? (
            <Stack spacing={2} sx={{ mt: 3 }}>
              {config.advancedFilterRows.map((row, rowIndex) => (
                <Stack
                  key={rowIndex}
                  direction="row"
                  spacing={2}
                  flexWrap="wrap"
                  useFlexGap
                  alignItems="center"
                >
                  {renderFilterRow(row)}
                </Stack>
              ))}
            </Stack>
          ) : null}

          {/* Active Filters Display */}
          {renderFilterChips()}
        </Stack>
      </Box>
    </LocalizationProvider>
  )
}
